import { Check, ChevronsUpDown, Database } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useTenant, type TenantSummary } from '@/providers/tenant-context';

function TenantIcon({ tenant, className }: { tenant: TenantSummary; className?: string }) {
  return (
    <Avatar className={cn('h-6 w-6 rounded-md', className)}>
      {tenant.logoUrl ? <AvatarImage src={tenant.logoUrl} alt={tenant.name} /> : null}
      <AvatarFallback className="rounded-md bg-primary/10 text-primary">
        <Database className="h-3.5 w-3.5" />
      </AvatarFallback>
    </Avatar>
  );
}

/**
 * Переключатель активной БД (тенанта) в хидере.
 *
 * Виден ТОЛЬКО когда у админа доступ более чем к одной БД (tenants.length > 1).
 * Однотенантный админ не видит переключатель — UX идентичен до-tenant версии.
 */
export function TenantSwitcher() {
  const { activeTenant, tenants, hasMultipleTenants, switchTenant, isSwitching } = useTenant();

  if (!hasMultipleTenants || !activeTenant) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={isSwitching}
          className="gap-2 max-w-[220px]"
        >
          <TenantIcon tenant={activeTenant} />
          <span className="truncate font-medium">{activeTenant.name}</span>
          <Badge variant="secondary" className="hidden sm:inline-flex shrink-0">
            БД
          </Badge>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel>Активная база данных</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {tenants.map((tenant) => {
          const isActive = tenant.slug === activeTenant.slug;
          return (
            <DropdownMenuItem
              key={tenant.slug}
              disabled={isSwitching}
              onSelect={(event) => {
                event.preventDefault();
                if (!isActive) {
                  void switchTenant(tenant.slug);
                }
              }}
              className="gap-2"
            >
              <TenantIcon tenant={tenant} />
              <span className="flex-1 truncate">{tenant.name}</span>
              {isActive ? <Check className="h-4 w-4 text-primary" /> : null}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
